import { IButtonProps } from '@honeybook/hbui/dist/components/HBButton/HBButton';
import { IconProps } from '@honeybook/icons/dist/types/Icon.interface';
import { Enumify } from '~/types/app.types';

export enum TemplatePanel {
    All_flows = 'All_flows',
    Starred = 'Starred',
    My_flows = 'My_flows',
    Shared = 'Shared',
    Lead_forms = 'Lead_forms'
}

export type TTemplatePanel = keyof typeof TemplatePanel;

// Template file is for non-flow templates
export enum FileTemplatePanel {
    Emails = 'Emails',
    Packages = 'Packages',
    Contact_forms = 'Contact_forms',
    Gift_cards = 'Gift_cards',
    Email_signature = 'Email_signature',
    Brochures = 'Brochures',
    Invoices = 'Invoices',
    Proposals = 'Proposals',
    Questionnaires = 'Questionnaires',
    Agreements = 'Agreements',
    Blocks = 'Blocks',
    All = 'All'
}

export const TemplateTypes = {
    EMAIL: 'Email',
    PACKAGE: 'Package',
    CONTACT_FORM: 'Contact form',
    GIFT_CARD: 'Gift card',
    EMAIL_SIGNATURE: 'Email signature',
    BROCHURE: 'Brochure',
    FLOW: 'Flow',
    CONTRACT: 'Contract',
    INVOICE: 'Invoice',
    QUESTIONNAIRE: 'Questionnaire',
    PROPOSAL: 'Proposal',
    BLOCK: 'Block'
} as const;

export const TemplateViewModelNames = {
    EMAIL: 'email',
    PACKAGE: 'package',
    CONTACT_FORM: 'contact form',
    GIFT_CARD: 'gift card',
    EMAIL_SIGNATURE: 'email signature',
    BROCHURE: 'brochure',
    FLOW: 'Flow',
    INVOICE: 'invoice',
    QUESTIONNAIRE: 'questionnaire',
    PROPOSAL: 'proposal',
    AGREEMENT: 'agreement',
    TIMELINE: 'timeline'
} as const;

export type TTemplateViewModelNames = Enumify<typeof TemplateViewModelNames>;

export const TemplateViewModelKeys = {
    EMAIL: 'email',
    PACKAGE: 'package',
    CONTACT_FORM: 'contactForm',
    GIFT_CARD: 'giftCard',
    EMAIL_SIGNATURE: 'emailSignature',
    BROCHURE: 'brochure',
    FLOW: 'flow',
    INVOICE: 'invoice',
    QUESTIONNAIRE: 'questionnaire',
    PROPOSAL: 'proposal',
    AGREEMENT: 'agreement',
    TIMELINE: 'timeline'
} as const;

export type TTemplateType = Enumify<typeof TemplateTypes>;

export type TTemplateViewModelKey = Enumify<typeof TemplateViewModelKeys>;

export type TFileTemplatePanel = keyof typeof FileTemplatePanel;

interface ITemplateMenuBase {
    label: string;
    icon?: (props: IconProps) => JSX.Element;
    analytics?: {
        source: string;
    };
}

export interface ITemplateMenuItem extends ITemplateMenuBase {
    section: TTemplatePanel;
    badge?: JSX.Element;
}

export interface IFileTemplateMenuItem extends ITemplateMenuBase {
    section: TFileTemplatePanel;
}

export interface IEmptyStateTemplatePanel {
    title: string;
    desc: string;
    icon: string;
    analytics: {
        source: string;
    };
    primary_cta?: string;
    secondary_cta?: {
        text: string;
        icon?: (iconProps: IconProps) => JSX.Element;
        iconPosition?: IButtonProps['iconPosition'];
        isUpperCase?: boolean;
    };
    link_txt?: string;
    link_url?: string;
}

// If one of the types here changed, change also on
// template_repository_manager.rb - delete_blocks_from_template_for_custom_option
export enum CreateTemplateType {
    Brochures = 'Brochures',
    Contact_forms = 'Contact_forms',
    ContentApproval = 'ContentApproval',
    Contract = 'Contract',
    Custom = 'Custom',
    Emails = 'Emails',
    Gift_cards = 'Gift_cards',
    Invoice = 'Invoice',
    RecurringInvoice = 'RecurringInvoice',
    MoodBoard = 'MoodBoard',
    Packages = 'Packages',
    Proposal = 'Proposal',
    Questionnaire = 'Questionnaire',
    Scheduler = 'Scheduler',
    Lead_form = 'Lead_form',
    TestimonialRequest = 'Testimonial',
    LegacyInvoice = 'LegacyInvoice',
    LegacyContract = 'LegacyContract',
    LegacyProposal = 'LegacyProposal',
    LegacyQuestionnaire = 'LegacyQuestionnaire',
    LegacyBrochure = 'LegacyBrochure',
    Blocks = 'Blocks'
}

/* these flow templates ids are being used for flip to flow */
export enum CreateTemplateIds {
    Contract = '60e6efe95366840059b4867c',
    Invoice = '60eadfc9657fff00817abed2',
    BlankInvoice = '6396060c49d9ab002fe2c909',
    RecurringInvoice = '62e0f30427f7bd00339285b3',
    Proposal = '60eae1f7657fff00b7b2d63d',
    ProposalRecurringInvoice = '62e0f30b35259b002c328064',
    Questionnaire = '60eae3f4657fff00eaf6f914',
    Brochures = '615c5f6f77cb540073404faf',
    SessionSelection = '65bf79f3177f6e00254948fd',
    MoodBoard = '62f8b439d31353002de2e09a',
    Testimonial = '62f8b4410ad51a00fcceffd2',
    ContentApproval = '62f8b448fc097a002d554eda',
    Custom = '62f8b42e60297f01094cd2e2',
    Scheduler = '6371ff42fff9a5002ccc1035'
}

export enum CreateLeadFormTemplateIds {
    Services = '6476176ac48b39002dff9130',
    Scheduler = '64761748a63b3d002d27b425',
    Invoice = '6476178115704504e17b82d1'
}

export type TCreateTemplateType = keyof typeof CreateTemplateType;

export type TSortBy = 'date-asc' | 'date-desc' | 'name-asc';

export interface IFoldersUiPersistence {
    type: 'file' | 'folder' | 'panel';
    value: string;
}

export const FOLDERS_UI_PERSISTENCE_KEY = 'selectedTemplatePanel';

export const MY_TEMPLATES_LAYOUT_VIEW_MODE = 'myTemplatesLayoutViewMode';
export const IS_VISIT_IN_MY_TEMPLATES = 'isVisitInMyTemplates';
export const MEMBER_TEMPLATE_SHARING = 'member_template_sharing';
export const GALLERY_TEMPLATE = 'template_gallery';
export const TEMPLATE_SELLING = 'template_selling';

export const PRICED_TEMPLATE_OBJECT_TYPE = 'priced';

export const FREE_TEMPLATE_OBJECT_TYPE = 'free';

export const LEGACY_TEMPLATES_UI_PERSISTENCE_DEFAULT_VALUE = {
    filterKey: FileTemplatePanel.Invoices,
    sortKey: '-created_at',
    viewKey: 'card'
};
